import React from 'react'
import { fetcher } from 'app/hooks/fetcher'
import { Routes } from 'app/routes'
import { SWRConfig } from 'swr'
import ThemeProvider from 'theme/ThemeProvider'
import SocketProvider from './theme/SocketProvider'
import { useMemoryManager } from './utils/memoryUtils'

// Cache management with memory limits
let cacheCleanupInterval: NodeJS.Timeout | null = null
let beforeUnloadListener: ((event: BeforeUnloadEvent) => void) | null = null

const App = () => {
	// 메모리 관리 hook 사용
	useMemoryManager()
	
	return (
		<SWRConfig
			value={{
				fetcher: fetcher,
				dedupingInterval: 10000, // 10초 동안 동일한 요청 중복 방지 (향상)
				focusThrottleInterval: 10000, // 포커스 시 재호출 간격 제한
				loadingTimeout: 3000, // 로딩 타임아웃 설정
				revalidateOnFocus: false, // 포커스 시 재검증 비활성화
				revalidateOnReconnect: true, // 재연결 시 재검증 활성화 (네트워크 복구)
				shouldRetryOnError: false, // 에러 시 재시도 비활성화
				errorRetryCount: 1, // 에러 재시도 횟수 제한
				errorRetryInterval: 5000, // 에러 재시도 간격 증가
				provider: (): any => {
					// Initialize cache with size limit
					const cacheData = localStorage.getItem('app-cache')
					const initialEntries = cacheData ? JSON.parse(cacheData) : []
					const map = new Map(initialEntries.slice(-100)) // Limit to last 100 entries
					
					// Remove existing listener to prevent duplicates
					if (beforeUnloadListener) {
						window.removeEventListener('beforeunload', beforeUnloadListener)
					}
					
					// Create new listener
					beforeUnloadListener = () => {
						// Only keep recent cache entries to prevent memory bloat
						const recentEntries = Array.from(map.entries()).slice(-50)
						localStorage.setItem('app-cache', JSON.stringify(recentEntries))
					}
					
					window.addEventListener('beforeunload', beforeUnloadListener)
					
					// Clean up old cache entries every 5 minutes
					if (cacheCleanupInterval) {
						clearInterval(cacheCleanupInterval)
					}
					
					cacheCleanupInterval = setInterval(() => {
						if (map.size > 200) {
							const entries = Array.from(map.entries())
							map.clear()
							// Keep only the most recent 100 entries
							entries.slice(-100).forEach(([key, value]) => {
								map.set(key, value)
							})
						}
					}, 5 * 60 * 1000) // 5 minutes
					
					return map
				},
			}}>
			<ThemeProvider>
				<SocketProvider>
					<Routes />
				</SocketProvider>
			</ThemeProvider>
		</SWRConfig>
	)
}

export default App
